import { useDispatch } from "react-redux";
import Input from "./MaterialBalanceInput";
import type { IMaterialBalanceInputRow, pressureUnitId } from "../../interfaces/interfaces";
import { useAppSelector } from "../../store/hooks/hooks";
import { MIN_MATERIAL_BALANCE_ROWS, MAX_MATERIAL_BALANCE_ROWS, addRow, removeRow, setInputValue, setPMesurmentUnit, setQMesurmentUnit } from "../../store/slices/materialBalanceCalculatorSlice";
import DropdownMenu, { type IDDItem } from "../../components/common/DropdownMenu";
import { useMemo } from "react";
import { unitConverter } from "../../components/MeasurementUnits/UnitConverter";
import { useTranslation } from "react-i18next";

interface IRow extends IMaterialBalanceInputRow {
	index: number;
	canRemove: boolean;
}

const Row: React.FC<IRow> = ({ year, q, p, index, canRemove }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const onInputChange = (fieldName: string, value: number | null, isIncomplete?: boolean) => {
		dispatch(setInputValue({ index, key: fieldName as "year" | "q" | "p", value, isIncomplete }));
	};

	const pValue = useMemo(() => {
		if (p.currentValue == null)
			return null;

		return parseFloat(p.currentValue.toFixed(4));
	}, [p.currentValue]);

	const qValue = useMemo(() => {
		if (q.currentValue == null)
			return null;

		return parseFloat(q.currentValue.toFixed(4));
	}, [q.currentValue]);

	return (
		<div className="material-balance__input-row">
			<Input
				className="material-balance__input-cell year"
				value={year}
				onChange={onInputChange}
				name="year"
				id={"material-balance-input-year-" + index}
			/>
			<Input
				className="material-balance__input-cell q"
				value={qValue}
				onChange={onInputChange}
				name="q"
				id={q.id}
				error={q?.error ?? null}
				errorParams={q?.errorParams ?? null}
			/>
			<Input
				className="material-balance__input-cell p"
				value={pValue}
				onChange={onInputChange}
				name="p"
				id={p.id}
				error={p?.error ?? null}
				errorParams={p?.errorParams ?? null}
			/>
			<button
				type="button"
				className="material-balance__remove-row-btn"
				disabled={!canRemove}
				onClick={() => dispatch(removeRow({ index }))}
				aria-label={t("table.remove_row")}
				title={t("table.remove_row")}
			>−</button>
		</div>
	);
};

const InputTable = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const rows = useAppSelector(state => state.materialBalance.inputTable);
	const pUnit = useAppSelector(state => state.materialBalance.pMeasurementUnit);
	const qUnit = useAppSelector(state => state.materialBalance.qMeasurementUnit);
	const rowIndices = Object.keys(rows).map(Number);

	const ddInputs: IDDItem[] = useMemo(() => {
		const units = unitConverter.getUnitsByCategory("pressure");
		return units.map(unit => ({
			id: unit.id,
			translationKey: `measurement_units.${unit.category}.${unit.id}`
		} as IDDItem));

	}, []);

	const qDropdownItems: IDDItem[] = useMemo(() => {
		return qUnit.unitList.map(id => ({
			id,
			translationKey: `measurement_units.q.${id}`
		}));
	}, [qUnit.unitList]);

	const onQUnitSelect = (id: string) => {
		const unitId = qUnit.unitList.find(unit => unit === id);
		if (unitId) {
			dispatch(setQMesurmentUnit({ toUnitId: unitId }));
		}
	};

	const onPUnitSelect = (id: string) => {
		const pressureUnit = id as pressureUnitId;
		if (!pressureUnit) return;

		dispatch(setPMesurmentUnit({ toUnitId: pressureUnit }));
	};

	return (
		<div className="material-balance__input-table round-corner-container">
			<div className="material-balance__input-head">
				<div className="material-balance__input-cell year">Year</div>
				<div className="material-balance__input-cell q">Q</div>
				<div className="material-balance__input-cell p">P</div>
			</div>
			<div className="material-balance__input-units">
				<div className="material-balance__input-cell year"></div>
				<DropdownMenu className="material-balance__input-cell q"
					inputId={"material-balance-input-q"}
					items={qDropdownItems}
					selectedItemId={qUnit.currentUnitId ?? qUnit.baseUnitId}
					onSelect={onQUnitSelect}
				/>
				<DropdownMenu className="material-balance__input-cell p"
					inputId={"material-balance-input-p"}
					items={ddInputs}
					selectedItemId={pUnit.currentUnitId ?? pUnit.baseUnitId}
					onSelect={onPUnitSelect}
				/>
			</div>
			{Object.entries(rows).map(([key, row]) => (
				<Row key={key} index={Number(key)} canRemove={rowIndices.length > MIN_MATERIAL_BALANCE_ROWS} {...row} />
			))}
			<div className="material-balance__row-actions">
				<button
					type="button"
					className="material-balance__add-row-btn"
					disabled={rowIndices.length >= MAX_MATERIAL_BALANCE_ROWS}
					onClick={() => dispatch(addRow())}
					data-label={t("table.add_row")}
					aria-label={t("table.add_row")}
				>+</button>
			</div>
		</div>
	);
};

export default InputTable;